// Simple DOM-based toast helper — lightweight, no framework changes required
// type: 'info' (default) | 'error' | 'warning' | 'success'

// Deduplication: prevents multiple identical toasts from appearing simultaneously
const _activeToastKeys = new Set();

export function showToast(message, { duration = 3000, type = "info" } = {}) {
  try {
    if (typeof document === "undefined") return;

    const containerId = "photoflow-toast-container";
    let container = document.getElementById(containerId);
    if (!container) {
      container = document.createElement("div");
      container.id = containerId;
      Object.assign(container.style, {
        position: "fixed",
        top: "72px",
        right: "20px",
        // 20000 sits above all overlays and modals (10000) while staying
        // below the critical LoadingOverlay (2147483647).
        zIndex: 20000,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        pointerEvents: "none",
      });
      document.body.appendChild(container);
    }

    // Visual theme per type
    const themes = {
      error: {
        bg: "#fef2f2",
        border: "#fca5a5",
        titleColor: "#991b1b",
        msgColor: "#7f1d1d",
        icon: "✕",
        label: "Error",
      },
      warning: {
        bg: "#fffbeb",
        border: "#fcd34d",
        titleColor: "#92400e",
        msgColor: "#78350f",
        icon: "⚠",
        label: "Warning",
      },
      success: {
        bg: "#f0fdf4",
        border: "#86efac",
        titleColor: "#166534",
        msgColor: "#14532d",
        icon: "✓",
        label: "Success",
      },
      info: {
        bg: "#fff",
        border: "rgba(0,0,0,0.06)",
        titleColor: "#111827",
        msgColor: "#374151",
        icon: "ℹ",
        label: "Notification",
      },
    };
    const theme = themes[type] || themes.info;

    // Dedup check: skip if an identical toast is already visible
    const dedupKey = `${message}|${type}`;
    if (_activeToastKeys.has(dedupKey)) return;
    _activeToastKeys.add(dedupKey);

    const toast = document.createElement("div");
    toast.setAttribute("role", type === "error" ? "alert" : "status");
    toast.setAttribute("aria-live", type === "error" ? "assertive" : "polite");
    toast.className = "photoflow-toast";
    Object.assign(toast.style, {
      pointerEvents: "auto",
      minWidth: "260px",
      maxWidth: "380px",
      background: theme.bg,
      color: theme.titleColor,
      borderRadius: "8px",
      boxShadow: "0 6px 18px rgba(16,24,40,0.12)",
      padding: "12px 14px",
      border: `1px solid ${theme.border}`,
      borderLeft: `4px solid ${theme.border}`,
      opacity: "0",
      transform: "translateY(-6px)",
      transition: "opacity 200ms ease, transform 200ms ease",
      fontSize: "0.95rem",
      lineHeight: "1.2",
    });

    const header = document.createElement("div");
    Object.assign(header.style, {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: "600",
      marginBottom: "4px",
      color: theme.titleColor,
    });

    const iconSpan = document.createElement("span");
    iconSpan.textContent = theme.icon;
    iconSpan.setAttribute("aria-hidden", "true");

    const titleSpan = document.createElement("span");
    titleSpan.textContent = theme.label;

    header.appendChild(iconSpan);
    header.appendChild(titleSpan);

    const msg = document.createElement("div");
    msg.textContent = message || "";
    msg.style.color = theme.msgColor;
    msg.style.fontSize = "0.9rem";

    toast.appendChild(header);
    toast.appendChild(msg);

    container.appendChild(toast);

    // animate in
    requestAnimationFrame(() => {
      toast.style.opacity = "1";
      toast.style.transform = "translateY(0)";
    });

    const hide = () => {
      toast.style.opacity = "0";
      toast.style.transform = "translateY(-6px)";
      setTimeout(() => {
        try {
          container.removeChild(toast);
        } catch (e) {}
        _activeToastKeys.delete(dedupKey);
        // remove container if empty
        if (container && container.childElementCount === 0) {
          try {
            container.parentNode.removeChild(container);
          } catch (e) {}
        }
      }, 220);
    };

    const t = setTimeout(hide, duration);
    // allow manual dismiss on click
    toast.addEventListener("click", () => {
      clearTimeout(t);
      hide();
    });
  } catch (err) {
    // swallow errors — non-critical
  }
}

export default showToast;
